import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { AbacusControl, AbacusExperiment, AbacusVariant } from 'bernie-abacus';
import { AnalyticsStore } from 'bernie-core';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';
import { ViewExtraLarge, ViewLarge, Viewport, ViewSmall } from 'bernie-view';

import { SearchResultsStore } from 'stores/search-results-store';

import { SearchFormFields } from 'src/common/models';

import { classNames } from 'uitk-react-utils';
import { UitkSecondaryButton } from 'uitk-react-button';

import { FiltersToggle } from 'components/search/sort-filter/filters-toggle';
import { MapToggle } from 'components/search/map/map-toggle';
import { PlaybackFilter } from 'components/search/playback/playback-filter';
import { ResultsHeader, ResultsHeaderMessageAlignment } from './results-header';

export interface ToolsProps extends LocalizedComponentProps {
  analytics?: AnalyticsStore;
  className?: string;
  searchResults?: SearchResultsStore;
  withMapButton?: boolean;
  withFilterButton?: boolean;
}

export interface ToolsState {
  toolsActive: boolean;
}

@withLocalization
@inject('searchResults')
@observer
export class Tools extends React.Component<ToolsProps, ToolsState> {
  private searchToolsWrapper;

  public static defaultProps = {
    l10n: null,
    withMapButton: false,
    withFilterButton: false
  };

  constructor(props: ToolsProps) {
    super(props);

    this.state = { toolsActive: false };
    this.searchToolsWrapper = React.createRef();
  }

  public componentDidMount() {
    this.setState({
      toolsActive: true
    });
  }

  public render() {
    const { l10n, searchResults, className, withMapButton, withFilterButton } = this.props;

    if (!searchResults) {
      return null;
    }

    const { isLoading, loyaltyMessagingModel, searchCriteria, sortAndFilter, resultsSummary } = searchResults;
    const pills = sortAndFilter && sortAndFilter.pills;
    const toolsClasses = classNames('tools all-b-margin-three', { className });
    const filters = searchResults && searchResults.searchAndFilterDisplayOptions && searchResults.searchAndFilterDisplayOptions.filters;
    const popularFilters =
      sortAndFilter && sortAndFilter.options && sortAndFilter.options.popularFilter && sortAndFilter.options.popularFilter.options;
    const resultsHeaderProps = {
      resultsSummary,
      isLoading,
      l10n,
      loyaltyMessagingModel,
      searchCriteria
    };
    const actionsProps = {
      isActive: this.state.toolsActive,
      filters,
      dialogTitle: l10n.formatText('hotels.searchResults.filtersDialog.title'),
      withMapButton,
      withFilterButton
    };
    const shouldRenderPills = pills && pills.length > 0;

    if (popularFilters) {
      return (
        <section className={`${toolsClasses} m-t-margin-one`} ref={this.searchToolsWrapper.value}>
          <Actions {...actionsProps} />
          <AbacusExperiment name="PWA_SRP_QUICK_FILTERS">
            <AbacusControl>
              {shouldRenderPills && (
                <div className="all-b-padding-three">
                  <Viewport>
                    <ViewSmall>
                      <PlaybackFilter showQuickFilters={false} />
                    </ViewSmall>
                    <ViewLarge>
                      <PlaybackFilter showQuickFilters={false} wrapPills={true} />
                    </ViewLarge>
                  </Viewport>
                </div>
              )}
              <ResultsHeader messageAlignment={ResultsHeaderMessageAlignment.Left} {...resultsHeaderProps} />
            </AbacusControl>
            <AbacusVariant bucket={1}>
              <ResultsHeader messageAlignment={ResultsHeaderMessageAlignment.Left} {...resultsHeaderProps} classes="pwa-theme--grey-700" />
              <div className="all-t-padding-three">
                <Viewport>
                  <ViewSmall>
                    <PlaybackFilter showQuickFilters={true} />
                  </ViewSmall>
                  <ViewLarge>
                    <PlaybackFilter showQuickFilters={true} wrapPills={true} />
                  </ViewLarge>
                </Viewport>
              </div>
            </AbacusVariant>
          </AbacusExperiment>
        </section>
      );
    }

    return (
      <section className={`${toolsClasses} m-t-margin-one`} ref={this.searchToolsWrapper.value}>
        <Actions {...actionsProps} />
        {shouldRenderPills && (
          <div className="all-b-padding-three">
            <Viewport>
              <ViewSmall>
                <PlaybackFilter showQuickFilters={false} />
              </ViewSmall>
              <ViewLarge>
                <PlaybackFilter showQuickFilters={false} wrapPills={true} />
              </ViewLarge>
            </Viewport>
          </div>
        )}
        <ResultsHeader messageAlignment={ResultsHeaderMessageAlignment.Left} {...resultsHeaderProps} />
      </section>
    );
  }
}

const ActionsComponent = ({ isActive, filters, dialogTitle, withFilterButton, withMapButton }: ActionsComponentProps) => (
  <div className={`uitk-flex uitk-flex-gap-two tools__actions all-b-padding-three`}>
    {withFilterButton && (
      <div className="uitk-flex-grow-2" data-stid="filters-toggle-button">
        <FiltersToggle
          buttonComponent={UitkSecondaryButton}
          isActive={isActive}
          isFullWidth={true}
          label="hotels.searchResults.openFiltersDialog.label"
          a11yMessage="hotels.searchResults.appliedFiltersCount.a11yLabel"
          rfrr=":FilterControl"
          stid="open-hotel-filters"
          filters={filters}
          dialogParam="filter"
          dialogTitle={dialogTitle}
        />
      </div>
    )}
    {withMapButton && (
      <div className="uitk-flex-grow-1" data-stid="map-toggle-button">
        <MapToggle isActive={isActive} />
      </div>
    )}
  </div>
);

interface ActionsComponentProps {
  isActive: boolean;
  filters: SearchFormFields;
  dialogTitle: string;
  withMapButton?: boolean;
  withFilterButton?: boolean;
  classes?: string;
}

const Actions = ({ isActive, filters, dialogTitle, withFilterButton, withMapButton }: ActionsExperimentProps) => (
  <Viewport>
    <ViewSmall>
      <ActionsComponent isActive={isActive} filters={filters} dialogTitle={dialogTitle} withFilterButton={true} withMapButton={true} />
    </ViewSmall>
    <ViewLarge>
      <span>{/*Removing this, will make whatever in view small show up in all breakpoints */}</span>
    </ViewLarge>
    <ViewExtraLarge>
      <ActionsComponent
        isActive={isActive}
        filters={filters}
        dialogTitle={dialogTitle}
        withMapButton={withMapButton}
        withFilterButton={withFilterButton}
      />
    </ViewExtraLarge>
  </Viewport>
);

interface ActionsExperimentProps {
  isActive: boolean;
  filters: SearchFormFields;
  dialogTitle: string;
  withMapButton?: boolean;
  withFilterButton?: boolean;
}
